import { Controller } from "@hotwired/stimulus"

import {
  canonicalValue,
  displayHour,
  formatDisplay,
  isTimeDisabled,
  meridiem,
  parseCanonical,
  to24Hour,
} from "./time_picker_helpers"

// Connects to data-controller="ui--time-picker"
//
// Primary time picker matching @a-safe-digital/react-time-picker: a read-only
// display input + clock trigger open a popover with scrollable hour/minute[/second]
// columns and, in 12h mode, an AM/PM toggle column. The columns are server-rendered
// (Ui::TimePickerComponent); this controller only updates selection/disabled state
// on the existing buttons, so there is no innerHTML replacement (and no CONEK-4920
// orphan-node hazard). A hidden input carries the canonical 24h value. CONEK-1580.
//
// The min/max bounds are reactive: a host may rewrite
// data-ui--time-picker-min-time-value / -max-time-value at runtime (e.g. an End
// picker bound to a Start picker) and the disabled options are re-applied without
// changing the value, writing the inputs, or emitting `change`. CONEK-7674.
export default class extends Controller {
  static targets = [
    "input",
    "hiddenInput",
    "trigger",
    "panel",
    "hourList",
    "minuteList",
    "secondList",
    "meridiemList",
  ]

  static values = {
    minuteInterval: Number,
    secondInterval: Number,
    hourFormat: Number,
    showSeconds: Boolean,
    minTime: Object,
    maxTime: Object,
    value: String,
  }

  connect() {
    this.hourFormat = this.hourFormatValue === 12 ? 12 : 24
    this.showSeconds = this.showSecondsValue
    this.minTime = this.normalizeBound(this.minTimeValue)
    this.maxTime = this.normalizeBound(this.maxTimeValue)
    this.parts = parseCanonical(this.valueValue)

    this._outsideClick = this.onOutsideClick.bind(this)
    this._connected = true
  }

  disconnect() {
    this._connected = false
    document.removeEventListener("click", this._outsideClick)
  }

  normalizeBound(obj) {
    return obj && obj.hour != null ? obj : null
  }

  // Stimulus fires these before connect(), hence the guard.
  minTimeValueChanged() {
    if (!this._connected) return
    this.minTime = this.normalizeBound(this.minTimeValue)
    this.paint()
  }

  maxTimeValueChanged() {
    if (!this._connected) return
    this.maxTime = this.normalizeBound(this.maxTimeValue)
    this.paint()
  }

  // --- Open / close ---

  toggle(event) {
    event.preventDefault()
    if (this.disabled) return
    if (this.isOpen()) this.close()
    else this.open()
  }

  open() {
    if (!this.hasPanelTarget) return
    this.panelTarget.classList.remove("hidden")
    this.position()
    document.addEventListener("click", this._outsideClick)
    // Wait for the panel to lay out, then center the selected values (React 50ms).
    setTimeout(() => this.scrollToSelected(), 50)
  }

  close() {
    if (!this.hasPanelTarget) return
    this.panelTarget.classList.add("hidden")
    document.removeEventListener("click", this._outsideClick)
  }

  isOpen() {
    return this.hasPanelTarget && !this.panelTarget.classList.contains("hidden")
  }

  onOutsideClick(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  onKeydown(event) {
    if (event.key === "Escape" && this.isOpen()) {
      this.close()
    } else if ((event.key === "Enter" || event.key === " ") && !this.isOpen()) {
      event.preventDefault()
      this.open()
    }
  }

  // Basic flip-up when there isn't room below the field.
  position() {
    const panel = this.panelTarget
    panel.classList.remove("bottom-full", "mb-2.5", "top-full", "mt-2.5")
    const rect = this.inputTarget.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    if (spaceBelow < panel.offsetHeight + 16 && rect.top > panel.offsetHeight) {
      panel.classList.add("bottom-full", "mb-2.5")
    } else {
      panel.classList.add("top-full", "mt-2.5")
    }
  }

  get disabled() {
    return this.hasInputTarget && this.inputTarget.disabled
  }

  // --- Selection ---

  selectHour(event) {
    if (event.currentTarget.disabled) return
    const hour = Number(event.currentTarget.dataset.hour)
    this.ensureParts()
    if (this.hourFormat === 12) {
      this.parts.hour = to24Hour(hour, meridiem(this.parts.hour))
    } else {
      this.parts.hour = hour
    }
    this.refresh()
  }

  selectMinute(event) {
    if (event.currentTarget.disabled) return
    this.ensureParts()
    this.parts.minute = Number(event.currentTarget.dataset.minute)
    this.refresh()
  }

  selectSecond(event) {
    if (event.currentTarget.disabled) return
    this.ensureParts()
    this.parts.second = Number(event.currentTarget.dataset.second)
    this.refresh()
  }

  selectMeridiem(event) {
    this.ensureParts()
    const period = event.currentTarget.dataset.meridiem
    this.parts.hour = to24Hour(displayHour(this.parts.hour, 12), period)
    this.refresh()
  }

  ensureParts() {
    if (!this.parts) this.parts = { hour: 0, minute: 0, second: 0 }
  }

  // Repaint the buttons, update the inputs, and notify listeners.
  refresh() {
    this.paint()
    const { hour, minute, second } = this.parts
    this.inputTarget.value = formatDisplay(
      hour,
      minute,
      second,
      this.hourFormat,
      this.showSeconds,
    )
    this.hiddenInputTarget.value = canonicalValue(
      hour,
      minute,
      second,
      this.showSeconds,
    )
    this.dispatch("change", { detail: { value: this.hiddenInputTarget.value } })
  }

  // Recompute selected + disabled state on every button only. No innerHTML is
  // replaced. An empty picker paints as 00:00 without mutating this.parts.
  paint() {
    const { hour, minute, second } = this.parts || {
      hour: 0,
      minute: 0,
      second: 0,
    }

    this.eachButton(this.hourListTarget, (btn) => {
      const h = Number(btn.dataset.hour)
      const selected =
        this.hourFormat === 12 ? h === displayHour(hour, 12) : h === hour
      const real = this.hourFormat === 12 ? to24Hour(h, meridiem(hour)) : h
      this.setState(
        btn,
        selected,
        isTimeDisabled(real, minute, 0, this.minTime, this.maxTime),
      )
    })

    this.eachButton(this.minuteListTarget, (btn) => {
      const m = Number(btn.dataset.minute)
      this.setState(
        btn,
        m === minute,
        isTimeDisabled(hour, m, 0, this.minTime, this.maxTime),
      )
    })

    if (this.showSeconds && this.hasSecondListTarget) {
      this.eachButton(this.secondListTarget, (btn) => {
        const s = Number(btn.dataset.second)
        this.setState(
          btn,
          s === second,
          isTimeDisabled(hour, minute, s, this.minTime, this.maxTime),
        )
      })
    }

    if (this.hourFormat === 12 && this.hasMeridiemListTarget) {
      this.eachButton(this.meridiemListTarget, (btn) => {
        this.setState(btn, btn.dataset.meridiem === meridiem(hour), false)
      })
    }
  }

  eachButton(list, fn) {
    list.querySelectorAll("button[role='option']").forEach(fn)
  }

  setState(btn, selected, disabled) {
    btn.dataset.selected = String(selected)
    btn.setAttribute("aria-selected", String(selected))
    btn.classList.toggle("bg-primary-600", selected)
    btn.classList.toggle("text-primary-foreground", selected)
    btn.classList.toggle("font-medium", selected)
    btn.classList.toggle("opacity-40", disabled)
    btn.classList.toggle("cursor-not-allowed", disabled)
    btn.classList.toggle("hover:bg-transparent", disabled)
    btn.disabled = disabled
  }

  scrollToSelected() {
    const lists = [this.hourListTarget, this.minuteListTarget]
    if (this.showSeconds && this.hasSecondListTarget)
      lists.push(this.secondListTarget)
    lists.forEach((list) => {
      const selected = list.querySelector('[data-selected="true"]')
      if (selected)
        selected.scrollIntoView({ block: "center", behavior: "instant" })
    })
  }
}
